// Helper per generare preventivi mock basati sui dati del cliente

/**
 * Genera un preventivo mock per Utilities (Luce/Gas)
 * @param {Object} cliente - Dati del cliente
 * @returns {Object} Preventivo generato
 */
export const generateUtilitiesMock = (cliente) => {
  return {
    tipoServizio: "Utilities",
    color: "667eea", // Viola
    badge: "Luce & Gas",
    dettagli: {
      "Intestatario": `${cliente?.nome || ""} ${cliente?.cognome || ""}`.trim() || "N/D",
      "Indirizzo": cliente?.slResIndirizzo 
        ? `${cliente.slResIndirizzoVia?.label || cliente.slResIndirizzoVia || ""} ${cliente.slResIndirizzo} ${cliente.slResCivico || ""}`.trim()
        : "N/D",
      "CAP": cliente?.slResCap || "N/D",
      "Email": cliente?.email || "N/D",
      "Telefono": cliente?.telefono || "N/D",
      "Servizio": "Luce + Gas",
      "Tipologia": "Privato",
      "Mercato": "Libero",
    },
    prezzo: 120.00,
    frequenza: "al mese",
    note: "Preventivo generato automaticamente. Modifica i dati per personalizzarlo.",
  };
};

/**
 * Genera un preventivo mock per TCM (Tutela Criticità Malattia)
 * @param {Object} cliente - Dati del cliente
 * @returns {Object} Preventivo generato
 */
export const generateTCMMock = (cliente) => {
  // Calcola età approssimativa se presente data di nascita
  let eta = "N/D";
  if (cliente?.dataNascita) {
    try {
      let dataNascita = cliente.dataNascita;
      if (typeof dataNascita === "string" && dataNascita.includes("/")) {
        const parts = dataNascita.split("/");
        if (parts.length === 3) {
          dataNascita = new Date(parts[2], parts[1] - 1, parts[0]);
        }
      } else if (typeof dataNascita === "string") {
        dataNascita = new Date(dataNascita);
      }
      
      if (dataNascita instanceof Date && !isNaN(dataNascita)) {
        const oggi = new Date();
        eta = oggi.getFullYear() - dataNascita.getFullYear();
        const mesiDiff = oggi.getMonth() - dataNascita.getMonth();
        if (mesiDiff < 0 || (mesiDiff === 0 && oggi.getDate() < dataNascita.getDate())) {
          eta--;
        }
        eta = `${eta} anni`;
      }
    } catch (e) {
      console.error("Errore calcolo età:", e);
    }
  }

  return {
    tipoServizio: "TCM",
    color: "13b86d", // Verde
    badge: "Temporanea Caso Morte",
    dettagli: {
      "Assicurato": `${cliente?.nome || ""} ${cliente?.cognome || ""}`.trim() || "N/D",
      "Data di nascita": cliente?.dataNascita || "N/D",
      "Età": eta,
      "Email": cliente?.email || "N/D",
      "Telefono": cliente?.telefono || "N/D",
      "Decesso accidentale": "€9,41 (Mass. 40.000€)",
      "Invalidità tot. perm. da infortunio": "€0,83",
      "Invalidità tot. perm. da malattia": "€9,62",
      "Inabilità tot. temp. da infortunio": "€63,88",
      "Inabilità tot. temp. da malattia": "€46,26",
      "Linea prodotto": "0006",
      "Massimale": "40",
    },
    prezzo: 130.00,
    frequenza: "al mese",
    note: "Piano Platinum con copertura completa.",
  };
};

/**
 * Genera un preventivo mock per Casa
 * @param {Object} cliente - Dati del cliente
 * @returns {Object} Preventivo generato
 */
export const generateCasaMock = (cliente) => {
  return {
    tipoServizio: "Casa",
    color: "1c75ff", // Blu
    badge: "Assicurazione Casa",
    dettagli: {
      "Proprietario": `${cliente?.nome || ""} ${cliente?.cognome || ""}`.trim() || "N/D",
      "Indirizzo": cliente?.slResIndirizzo 
        ? `${cliente.slResIndirizzoVia?.label || cliente.slResIndirizzoVia || ""} ${cliente.slResIndirizzo} ${cliente.slResCivico || ""}`.trim()
        : "N/D",
      "Provincia": cliente?.slResProvincia || "N/D",
      "Email": cliente?.email || "N/D",
      "Telefono": cliente?.telefono || "N/D",
      "Tipologia": "Abitazione principale",
      "Valore immobile": "€200.000",
    },
    prezzo: 25.00,
    frequenza: "al mese",
    note: "Preventivo generato automaticamente. Modifica i dati per personalizzarlo.",
  };
};

/**
 * Genera un preventivo mock per Vita Protetta
 * @param {Object} cliente - Dati del cliente
 * @returns {Object} Preventivo generato
 */
export const generateVitaProtettaMock = (cliente) => {
  // Calcola età approssimativa se presente data di nascita
  let eta = "N/D";
  if (cliente?.dataNascita) {
    try {
      let dataNascita = cliente.dataNascita;
      if (typeof dataNascita === "string" && dataNascita.includes("/")) {
        const parts = dataNascita.split("/");
        if (parts.length === 3) {
          dataNascita = new Date(parts[2], parts[1] - 1, parts[0]);
        }
      } else if (typeof dataNascita === "string") {
        dataNascita = new Date(dataNascita);
      }
      
      if (dataNascita instanceof Date && !isNaN(dataNascita)) {
        const oggi = new Date();
        eta = oggi.getFullYear() - dataNascita.getFullYear();
        const mesiDiff = oggi.getMonth() - dataNascita.getMonth();
        if (mesiDiff < 0 || (mesiDiff === 0 && oggi.getDate() < dataNascita.getDate())) {
          eta--;
        }
        eta = `${eta} anni`;
      }
    } catch (e) {
      console.error("Errore calcolo età:", e);
    }
  }

  return {
    tipoServizio: "Vita Protetta",
    color: "ff6f00", // Arancione
    badge: "Assicurazione Vita",
    dettagli: {
      "Assicurato": `${cliente?.nome || ""} ${cliente?.cognome || ""}`.trim() || "N/D",
      "Data di nascita": cliente?.dataNascita || "N/D",
      "Età": eta,
      "Email": cliente?.email || "N/D",
      "Telefono": cliente?.telefono || "N/D",
      "Decesso accidentale": "Massimale 40.000€",
      "Invalidità tot. permanente": "Massimale 40.000€ (condiviso)",
      "Inabilità tot. temporanea": "1000€ Protezione Reddito",
      "Protezione Bollette": "Fino a 1000€",
      "Protezione Premio": "Fino a 1000€",
    },
    prezzo: 130.00,
    frequenza: "all'anno",
    note: "Piano Platinum con copertura completa.",
  };
};
